/**
 * @file src/config/configurationPack.js
 * @description One shape for the things a person configures, and one place that decides whether the
 * answer came from the server, from this browser, or from nowhere at all.
 *
 * A user's configuration lives under `/me/users/configurations/`. It was read in four different
 * shapes by four different callers, each of which collapsed every possible failure into `null` or
 * `{}` — and `null` is also what "you never set anything" looks like. So a controller somebody had
 * mapped came back unmapped on a boot with no network, and nothing said why.
 *
 * ## Three ideas, and everything else follows from them
 *
 * **A read has an outcome, not just a value.** `{}` from a 204 and `{}` from a body that would not
 * parse are opposite facts: the first is the server saying "nothing here", the second is the server
 * saying nothing at all. Told apart, the mirror can clear a deleted mapping and refuse to overwrite
 * a good copy with a broken response. Collapsed together, a deleted mapping resurrects from disk on
 * every boot and one malformed reply wipes what was kept.
 *
 * **The mirror is written on WRITES too, not only reads.** Learning a CC is a PUT, and nothing GETs
 * afterwards. A mirror fed only by reads therefore lags every edit — the mapping you just made is
 * the one that is missing when you come back to it offline. Writing after the SERVER HAS ACCEPTED
 * is not an offline queue and reconciles nothing: there is still exactly one writer.
 *
 * **Every operation is FOR somebody, decided before it starts.** A request issued as one person
 * can land after somebody else has arrived — a shared browser, or an embed whose host swapped
 * tokens — and an answer filed under whoever happens to be here now is the first account's mappings
 * written into the second account's rows. So an operation CAPTURES the identity it is for
 * (`beginConfigurationOperation`) before it issues anything, and everything it later writes — the
 * page memo and the rows on disk — is refused if that identity has since changed. One rule, checked
 * in one place, rather than a guard per call site.
 *
 * ## What it does not do
 *
 * No offline writes, no queue, no replay, no last-write-wins. An offline write still meets the 503
 * `offline-read-only` door in `httpClient.js`, which is the honest answer while there is only a
 * cached reader and no second writer to disagree with.
 *
 * And nothing here reconciles by time. `observedAt` on a row is when THIS BROWSER SAW a value; the
 * endpoints carry no revision and no ETag, so there is no way to ask which of two values is newer.
 * The field is for saying how old a fallback is, never for choosing between two of them.
 */
import {
  readUserConfiguration,
  readConfigurationOwnerKey,
  writeUserConfigurations,
  deleteUserConfiguration,
  RELEASE_EFFECTIVE_SETTINGS_KIND,
} from '../offline/offlineLibraryStore.js';

/**
 * What a read actually found. Every configuration service answers in these terms.
 *
 * The two that mean "believe it" are `NETWORK` and `ABSENT` — the server spoke — and nothing else.
 * `UNAUTHORIZED` is deliberately NOT a reason to serve the mirror: account state must not be handed
 * to a session whose identity the server has just declined to confirm.
 */
export const READ_OUTCOME = Object.freeze({
  /** The server answered with a value. Authoritative — mirror it. */
  NETWORK: 'network',
  /** The server answered that there is nothing. Authoritative — clear the mirror. */
  ABSENT: 'absent',
  /** No answer: offline, a timeout, a 5xx. Serve the mirror; change nothing on disk. */
  UNAVAILABLE: 'unavailable',
  /** 401/403. Serve NOTHING — an unverified identity does not get to read account state. */
  UNAUTHORIZED: 'unauthorized',
  /** Abandoned — the caller cancelled, or the person it was being read for left. Not a fact about
   *  the data; touch nothing, and hand the caller nothing to apply. */
  ABORTED: 'aborted',
  /** A response that would not parse. Never overwrite a good mirror with it. */
  INVALID_RESPONSE: 'invalid-response',
});

/** Where the value a caller is holding actually came from. */
export const VALUE_SOURCE = Object.freeze({
  NETWORK: 'network',
  DISK: 'disk',
  /** Nothing was found anywhere — which is not the same as an empty answer. */
  NONE: 'none',
});

/**
 * The kinds this mirror knows how to hold.
 *
 * Adding a kind is one line here plus a read that goes through {@link readThrough}. This is a
 * REGISTRY, not enforcement — it can only list what registered — so the thing that keeps a fifth
 * caller from quietly fetching a configuration endpoint on its own is the static import rule in
 * `tests/config/configurationEndpointOwnership.test.js`, not this object.
 */
export const CONFIGURATION_KINDS = Object.freeze({
  /** One MIDI slice per scope key (`orbiter:<id>`, `collection:<id>`), exactly as the server
   *  returns it. The collection MIDI-scope PREFERENCE rides inside its collection's slice, where
   *  the server keeps it — splitting it out would make two cached truths of one snapshot. */
  MIDI: 'midi',
  /** The viewer's resolved track sync, taken out of a stored release so the release row can go back
   *  to being what its key says it is: shared. Written by `offlineLibraryStore` itself. */
  EFFECTIVE_SETTINGS: RELEASE_EFFECTIVE_SETTINGS_KIND,
  /** One collection's arrangement AS THIS PERSON LEFT IT, keyed by collection id — the stages and
   *  rail a viewer arranged for themselves, as opposed to the collection's own arrangement, which
   *  its editors write and everyone reads. See `api/collectionArrangementService.js`. */
  ARRANGEMENT: 'collection-arrangement',
});

/**
 * Values resolved in THIS page, so the two callers that want the same slice cost one request.
 *
 * The MIDI-scope preference and the mapping slice are literally the same server snapshot, fetched
 * twice by two modules that never knew about each other. Keyed by kind and resource, replaced on
 * any accepted write to that resource, and dropped whole on every change of identity.
 * @type {Map<string, {value: any, source: string, outcome: string, observedAt: number}>}
 */
const resolved = new Map();
/** In-flight reads, so two callers in the same tick share one request rather than racing it. */
const inFlight = new Map();

/**
 * Which person this page is configuring for, as a number that changes when they do.
 *
 * The same pattern the offline downloads use, for the same reason: a promise cannot be un-started.
 * Clearing the maps below stops NEW reads from seeing the previous person's answers, but a request
 * already out keeps running, and its continuation would happily write into the maps and onto disk
 * afterwards. Captured at the start of an operation and re-checked before anything is kept, this
 * number is what tells that continuation it is answering for somebody who has left.
 */
let identityEpoch = 0;

/** The epoch an operation starting NOW belongs to. */
export function configurationEpoch() {
  return identityEpoch;
}

/** Whether the person an operation started for is still the person here. */
export function isConfigurationEpochCurrent(epoch) {
  return epoch === identityEpoch;
}

/**
 * Begin an operation on somebody's configuration: settle WHO it is for before it issues anything.
 *
 * Call this BEFORE the request, never after. A caller that starts a GET and asks afterwards gets
 * the identity that arrived DURING the request, which is exactly the one the answer must not be
 * filed under. The owner key it returns is the store-level backstop — a write handed the wrong one
 * is dropped inside the transaction rather than misfiled — and the epoch covers everything else,
 * including the page memo, which has no owner in it at all.
 *
 * @returns {Promise<{epoch: number, ownerKey: string|null}>}
 */
export async function beginConfigurationOperation() {
  const epoch = identityEpoch;
  // Waits on the owner reconcile (inside `readConfigurationOwnerKey`), so an operation begun while
  // a change of identity is being written down asks after it, not during it.
  const ownerKey = await readConfigurationOwnerKey().catch(() => null);
  return { epoch, ownerKey };
}

/** An answer for somebody who is no longer here. Nothing is kept, and the caller applies nothing. */
function abandoned() {
  return { value: null, source: VALUE_SOURCE.NONE, outcome: READ_OUTCOME.ABORTED, observedAt: 0 };
}

/**
 * The outcomes the SERVER stated, which are the only ones worth remembering for the life of a page.
 *
 * Anything else is a fact about this moment rather than about the data: a slice served from the
 * mirror while offline has to be asked for again once there is a network, and a session whose
 * identity the server declined has to be able to ask again once it is signed in. Remembered, they
 * became permanent — a mapping deleted on another device stayed live until the tab was closed.
 */
const AUTHORITATIVE_OUTCOMES = new Set([READ_OUTCOME.NETWORK, READ_OUTCOME.ABSENT]);

function memoKey(kind, resourceKey) {
  return `${kind} ${resourceKey}`;
}

/**
 * Forget everything this page resolved.
 *
 * Called at both identity edges in `src/auth/sessionAuth.js`, beside the other invalidations. The
 * rows on disk are owner-keyed and take care of themselves; these in-memory ones are not, and a
 * session that switched accounts while holding the previous account's mappings would apply them.
 */
export function resetConfigurationPack() {
  // First, so that everything already in flight is answering for the previous person from here on
  // — clearing the maps alone would leave those continuations free to refill them.
  identityEpoch += 1;
  resolved.clear();
  inFlight.clear();
}

/** What this page already resolved for one resource, or null when it has not asked yet. */
export function peekResolvedConfiguration(kind, resourceKey) {
  if (!kind || !resourceKey) return null;
  return resolved.get(memoKey(kind, resourceKey)) || null;
}

/**
 * Drop every resolved value of one kind, so the next read of each asks again.
 *
 * For the one case the memo cannot reason about: somebody changed this configuration somewhere
 * ELSE, and this page has been told to go and look. Nothing local changed, so nothing here knows
 * to be out of date.
 */
export function forgetResolvedKind(kind) {
  if (!kind) return;
  for (const key of [...resolved.keys()]) {
    if (key.startsWith(`${kind} `)) resolved.delete(key);
  }
}

/**
 * Run one configuration read and keep the mirror in step with what it found.
 *
 * @param {object} args
 * @param {string} args.kind one of {@link CONFIGURATION_KINDS}
 * @param {string} args.resourceKey what this value is about, within the kind
 * @param {() => Promise<{outcome: string, value?: any}>} args.read the service call, in outcome terms
 * @param {{epoch: number, ownerKey: string|null}} [args.identity] who the read is for, when the
 *   caller ISSUED THE REQUEST ITSELF and had to capture that before it went out. Left out, the read
 *   is started here and the identity is captured here, which is only right when `read` is what
 *   issues it.
 * @returns {Promise<{value: any, source: string, outcome: string, observedAt: number}>}
 */
export async function readThrough({ kind, resourceKey, read, identity = null }) {
  if (!kind || !resourceKey || typeof read !== 'function') {
    return { value: null, source: VALUE_SOURCE.NONE, outcome: READ_OUTCOME.UNAVAILABLE, observedAt: 0 };
  }

  const key = memoKey(kind, resourceKey);
  const already = resolved.get(key);
  if (already) return already;
  const pending = inFlight.get(key);
  if (pending) return pending;

  const work = (async () => {
    const { epoch, ownerKey } = identity || (await beginConfigurationOperation());
    let outcome = READ_OUTCOME.UNAVAILABLE;
    let value = null;
    try {
      const answer = await read();
      outcome = answer?.outcome || READ_OUTCOME.UNAVAILABLE;
      value = answer?.value ?? null;
    } catch {
      // A service that throws where it should have said `unavailable` still must not take the
      // session down, and must not be read as an authoritative empty.
      outcome = READ_OUTCOME.UNAVAILABLE;
    }

    // Everything below either writes to this browser or hands the caller something to apply, and
    // somebody else is here now. Both are for the person who has left.
    if (!isConfigurationEpochCurrent(epoch)) return abandoned();

    const answer = await settle({ kind, resourceKey, outcome, value, ownerKey });
    // And again, because `settle` waits too: it writes the row, or deletes it, or reads the mirror
    // off disk, and somebody else can arrive inside any of those. The check above only covers the
    // request; this one covers the answer, and it is the one that reaches the CALLER — a stale
    // value handed back is not merely remembered for the next reader, it is applied. That is how
    // the previous account's saved MIDI-scope preference was put on the collection the arriving
    // account is looking at. Nothing is kept either, since the memo has no owner in it to catch
    // this later.
    if (!isConfigurationEpochCurrent(epoch)) return abandoned();
    if (AUTHORITATIVE_OUTCOMES.has(answer.outcome)) resolved.set(key, answer);
    return answer;
  })();

  inFlight.set(key, work);
  try {
    return await work;
  } finally {
    // Only if it is still OURS. A read abandoned by a change of identity resolves after the next
    // person's read for the same slice has registered, and deleting that one would leave a second
    // caller starting a request of its own.
    if (inFlight.get(key) === work) inFlight.delete(key);
  }
}

/**
 * What one outcome means for the copy on this browser, and what the caller is handed.
 * @returns {Promise<{value: any, source: string, outcome: string, observedAt: number}>}
 */
async function settle({ kind, resourceKey, outcome, value, ownerKey }) {
  if (outcome === READ_OUTCOME.NETWORK) {
    await writeUserConfigurations([{ kind, resourceKey, value }], { expectedOwnerKey: ownerKey })
      .catch(() => {});
    return { value, source: VALUE_SOURCE.NETWORK, outcome, observedAt: Date.now() };
  }

  if (outcome === READ_OUTCOME.ABSENT) {
    // The server states the absence. Keeping the row would resurrect, on every later boot, a
    // mapping somebody deleted. Filed against the owner this read STARTED for, like the write
    // above: the epoch catches a late answer, but only until this call blocks on the owner gate,
    // and a reconcile landing inside that window would take the arriving person's row.
    await deleteUserConfiguration(kind, resourceKey, { expectedOwnerKey: ownerKey })
      .catch(() => {});
    return { value: null, source: VALUE_SOURCE.NETWORK, outcome, observedAt: Date.now() };
  }

  if (outcome === READ_OUTCOME.UNAUTHORIZED) {
    // Nothing from disk. The rows are account state and the server has just declined to confirm
    // whose account this is.
    return { value: null, source: VALUE_SOURCE.NONE, outcome, observedAt: 0 };
  }

  const mirrored = await readUserConfiguration(kind, resourceKey).catch(() => null);
  if (!mirrored) {
    return { value: null, source: VALUE_SOURCE.NONE, outcome, observedAt: 0 };
  }
  return {
    value: mirrored.value,
    source: VALUE_SOURCE.DISK,
    outcome,
    observedAt: mirrored.observedAt,
  };
}

/**
 * The value this browser holds for one resource without asking anybody: what this page resolved,
 * and failing that what is mirrored on disk for whoever the owner row names.
 *
 * It is what a write has to build on. A caller that rebuilds a value from the page memo alone
 * starts from nothing whenever this page never resolved that resource — after an identity change,
 * after the memo was dropped, or simply on a resource nothing here has read — and then PUTs that
 * nothing over a row that held everything.
 * @returns {Promise<any>} the value, or null when there is none anywhere
 */
export async function readHeldConfigurationValue(kind, resourceKey) {
  if (!kind || !resourceKey) return null;
  const held = resolved.get(memoKey(kind, resourceKey));
  if (held) return held.value ?? null;
  const mirrored = await readUserConfiguration(kind, resourceKey).catch(() => null);
  return mirrored ? mirrored.value ?? null : null;
}

/**
 * Record a value the SERVER HAS ALREADY ACCEPTED.
 *
 * This is the half a read-through cache does not have. Nothing GETs after a successful PUT, so a
 * mirror fed by reads alone is stale from the moment somebody edits anything.
 *
 * The identity is the one the ACCEPTED REQUEST was made for, captured before it went out — the
 * server accepted this value for that person, and it belongs to them however long the round trip
 * took and whoever is here by the time it lands.
 * @param {{kind: string, resourceKey: string, value: any,
 *   identity: {epoch: number, ownerKey: string|null}}} entry
 * @returns {Promise<boolean>} whether the row reached disk
 */
export async function mirrorAccepted({ kind, resourceKey, value, identity }) {
  if (!kind || !resourceKey || !identity) return false;
  if (!isConfigurationEpochCurrent(identity.epoch)) return false;
  resolved.set(memoKey(kind, resourceKey), {
    value,
    source: VALUE_SOURCE.NETWORK,
    outcome: READ_OUTCOME.NETWORK,
    observedAt: Date.now(),
  });
  const written = await writeUserConfigurations([{ kind, resourceKey, value }], {
    expectedOwnerKey: identity.ownerKey,
  }).catch(() => 0);
  return written > 0;
}
